/**
 * AgentLayout - Complete agent workspace layout with proper navigation
 * Includes breadcrumbs, back navigation, responsive sidebar, and quick actions
 * PERMISSION-GATED: Only shows menu items for granted permissions
 */
import { ReactNode, useState, useMemo } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/hooks/useAuth';
import { useAgentPermissions, type AgentPermission } from '@/hooks/useAgentPermissions';
// TimeWidget removed - using SimpleClockWidget in dashboard only
import { Button } from '@/components/ui/button';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { 
  LayoutDashboard, 
  Ticket, 
  LogOut, 
  User, 
  Settings,
  Bell,
  ChevronDown,
  Headphones,
  Calendar,
  Award,
  BookOpen,
  GraduationCap,
  Clock,
  Megaphone,
  Target,
  Menu,
  ChevronLeft,
  ChevronRight,
  Home,
  Search,
  HelpCircle,
  MessageSquare,
  Activity,
  type LucideIcon
} from 'lucide-react';
import { cn } from '@/lib/utils';

interface AgentLayoutProps {
  children: ReactNode;
}

interface NavItem {
  id: string;
  href: string;
  label: string;
  icon: LucideIcon;
  badge?: string;
  /** Permission required to see this menu item */
  permission?: AgentPermission;
}

interface NavSection {
  id: string;
  title: string;
  items: NavItem[];
}

// Navigation sections with permission requirements - uses lang prefix
const getNavSections = (langPrefix: string): NavSection[] => [
  {
    id: 'workspace',
    title: 'My Workspace',
    items: [
      { id: 'dashboard', href: `${langPrefix}/agent`, label: 'Dashboard', icon: LayoutDashboard },
      { id: 'tickets', href: `${langPrefix}/agent/tickets`, label: 'My Tickets', icon: Ticket, permission: 'view_case' },
    ],
  },
  {
    id: 'channels',
    title: 'Channels',
    items: [
      { id: 'phone', href: `${langPrefix}/agent/phone`, label: 'Phone', icon: Headphones, permission: 'use_dialpad' },
      // Email visibility: all agents can see; sending gated by myEmailConfig?.is_active inside page
      { id: 'mail', href: `${langPrefix}/agent/mail`, label: 'Email', icon: MessageSquare },
    ],
  },
  {
    id: 'planning',
    title: 'Schedule',
    items: [
      { id: 'schedule', href: `${langPrefix}/agent/schedule`, label: 'My Schedule', icon: Calendar, permission: 'view_schedule' },
      { id: 'time', href: `${langPrefix}/agent/time`, label: 'Time Tracking', icon: Clock, permission: 'clock_in_out' },
    ],
  },
  {
    id: 'quality',
    title: 'Quality & Training',
    items: [
      { id: 'qa', href: `${langPrefix}/agent/qa`, label: 'My Evaluations', icon: Award, permission: 'view_own_evaluations' },
      { id: 'coaching', href: `${langPrefix}/agent/coaching`, label: 'Coaching', icon: Target, permission: 'acknowledge_coaching' },
      { id: 'learning', href: `${langPrefix}/agent/learning`, label: 'Training', icon: GraduationCap, permission: 'acknowledge_coaching' },
    ],
  },
  {
    id: 'resources',
    title: 'Resources',
    items: [
      { id: 'kb', href: `${langPrefix}/agent/kb`, label: 'Knowledge Base', icon: BookOpen, permission: 'view_kb_articles' },
      { id: 'announcements', href: `${langPrefix}/agent/announcements`, label: 'Announcements', icon: Megaphone },
    ],
  },
];

// Generate breadcrumbs from current path
function getBreadcrumbs(pathname: string, sections: NavSection[], langPrefix: string): { label: string; href: string }[] {
  const agentHome = `${langPrefix}/agent`;
  const crumbs: { label: string; href: string }[] = [
    { label: 'Home', href: agentHome }
  ];

  // Find the current page in nav items
  for (const section of sections) {
    for (const item of section.items) {
      if (pathname === item.href || pathname.startsWith(item.href + '/')) {
        if (item.href !== agentHome) {
          crumbs.push({ label: item.label, href: item.href });
        }
        break;
      }
    }
  }

  return crumbs;
}

export function AgentLayout({ children }: AgentLayoutProps) {
  const location = useLocation();
  const navigate = useNavigate();
  const { profile, signOut } = useAuth();
  const { hasPermission, isLoading: permissionsLoading } = useAgentPermissions();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  // Extract language from URL path (e.g., /fr/agent → /fr)
  const langPrefix = useMemo(() => {
    const segments = location.pathname.split('/').filter(Boolean);
    return segments.length > 0 ? `/${segments[0]}` : '/en';
  }, [location.pathname]);

  const navSections = useMemo(() => getNavSections(langPrefix), [langPrefix]);
  const breadcrumbs = getBreadcrumbs(location.pathname, navSections, langPrefix);
  const canGoBack = location.pathname !== `${langPrefix}/agent`;

  // Filter nav sections based on user permissions
  const filteredNavSections = useMemo(() => {
    if (permissionsLoading) return [];
    
    return navSections
      .map(section => ({
        ...section,
        items: section.items.filter(item => {
          // If no permission required, show the item
          if (!item.permission) return true;
          // Check if user has the required permission
          return hasPermission(item.permission);
        })
      }))
      // Remove sections with no visible items
      .filter(section => section.items.length > 0);
  }, [hasPermission, permissionsLoading, navSections]);

  const handleSignOut = async () => {
    await signOut();
    navigate(`${langPrefix}/auth`);
  };

  const getInitials = (name: string) => {
    return name
      .split(' ')
      .map(n => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const isActive = (href: string) => {
    if (href === `${langPrefix}/agent`) return location.pathname === `${langPrefix}/agent`;
    return location.pathname.startsWith(href);
  };

  const SidebarContent = ({ mobile = false }: { mobile?: boolean }) => (
    <div className={cn("flex flex-col h-full", mobile && "pt-4")}>
      {/* Logo */}
      <div className={cn(
        "px-4 pb-4 border-b border-border",
        sidebarCollapsed && !mobile && "px-2"
      )}>
        <Link to={`${langPrefix}/agent`} className="flex items-center gap-2">
          <div className="w-10 h-10 bg-gradient-to-br from-primary to-primary/80 rounded-lg flex items-center justify-center flex-shrink-0">
            <Headphones className="h-5 w-5 text-primary-foreground" />
          </div>
          {(!sidebarCollapsed || mobile) && (
            <div>
              <span className="text-lg font-bold text-foreground">
                Call<span className="text-primary">IT</span><span className="text-accent">Dev</span>
              </span>
              <p className="text-xs text-muted-foreground">Agent Workspace</p>
            </div>
          )}
        </Link>
      </div>

      {/* Search */}
      {(!sidebarCollapsed || mobile) && (
        <div className="p-4">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Rechercher..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="pl-9 bg-muted/50 border-0"
            />
          </div>
        </div>
      )}

      {/* Navigation */}
      <ScrollArea className="flex-1 px-2">
        {filteredNavSections.map((section) => (
          <div key={section.id} className="mb-4">
            {(!sidebarCollapsed || mobile) && (
              <p className="px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider">
                {section.title}
              </p>
            )}
            <ul className="space-y-1">
              {section.items.map((item) => {
                const active = isActive(item.href);
                return (
                  <li key={item.id}>
                    <Link
                      to={item.href}
                      onClick={() => mobile && setMobileMenuOpen(false)}
                      className={cn(
                        'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors',
                        sidebarCollapsed && !mobile && 'justify-center px-2',
                        active
                          ? 'bg-primary text-primary-foreground shadow-sm'
                          : 'text-muted-foreground hover:text-foreground hover:bg-muted'
                      )}
                    >
                      <item.icon className={cn("h-5 w-5 flex-shrink-0", active && "text-primary-foreground")} />
                      {(!sidebarCollapsed || mobile) && (
                        <>
                          <span className="flex-1">{item.label}</span>
                          {item.badge && (
                            <Badge variant={active ? "secondary" : "outline"} className="text-xs">
                              {item.badge}
                            </Badge>
                          )}
                        </>
                      )}
                    </Link>
                  </li>
                );
              })}
            </ul>
          </div>
        ))}
      </ScrollArea>

      {/* Quick Actions */}
      {(!sidebarCollapsed || mobile) && (
        <div className="p-4 border-t border-border space-y-2">
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => navigate(`${langPrefix}/agent/kb`)}
          >
            <HelpCircle className="h-4 w-4 mr-2" />
            Help & Support
          </Button>
          <Button 
            variant="outline" 
            className="w-full justify-start"
            onClick={() => navigate(`${langPrefix}/agent/settings`)}
          >
            <Settings className="h-4 w-4 mr-2" />
            Settings
          </Button>
        </div>
      )}

      {/* Collapse Toggle (Desktop only) */}
      {!mobile && (
        <div className="p-2 border-t border-border">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className="w-full justify-center text-muted-foreground"
          >
            {sidebarCollapsed ? <ChevronRight className="h-4 w-4" /> : <ChevronLeft className="h-4 w-4" />}
          </Button>
        </div>
      )}
    </div>
  );

  return (
    <div className="min-h-screen bg-background flex">
      {/* Desktop Sidebar */}
      <aside className={cn(
        "hidden lg:flex flex-col border-r border-border bg-card transition-all duration-300",
        sidebarCollapsed ? "w-16" : "w-64"
      )}>
        <SidebarContent />
      </aside>

      {/* Main Content Area */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="sticky top-0 z-50 bg-card border-b border-border">
          <div className="flex items-center justify-between h-16 px-4 lg:px-6">
            {/* Left: Mobile Menu + Breadcrumbs */}
            <div className="flex items-center gap-3">
              {/* Mobile Menu Trigger */}
              <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" className="lg:hidden">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
                <SheetContent side="left" className="w-72 p-0">
                  <SidebarContent mobile />
                </SheetContent>
              </Sheet>

              {/* Back Button */}
              {canGoBack && (
                <Button
                  variant="ghost"
                  size="icon"
                  onClick={() => navigate(-1)}
                  className="hidden sm:flex"
                >
                  <ChevronLeft className="h-5 w-5" />
                </Button>
              )}

              {/* Breadcrumbs */}
              <nav className="hidden sm:flex items-center gap-1 text-sm">
                {breadcrumbs.map((crumb, index) => (
                  <div key={crumb.href} className="flex items-center">
                    {index > 0 && <ChevronRight className="h-4 w-4 mx-1 text-muted-foreground" />}
                    {index === breadcrumbs.length - 1 ? (
                      <span className="font-medium text-foreground">{crumb.label}</span>
                    ) : (
                      <Link 
                        to={crumb.href} 
                        className="text-muted-foreground hover:text-foreground transition-colors"
                      >
                        {crumb.label}
                      </Link>
                    )}
                  </div>
                ))}
              </nav>

              {/* Mobile Title */}
              <span className="sm:hidden font-semibold text-foreground">
                {breadcrumbs[breadcrumbs.length - 1]?.label || 'Agent'}
              </span>
            </div>

            {/* Right: Actions */}
            <div className="flex items-center gap-2">
              {/* Status Indicator */}
              <div className="hidden md:flex items-center gap-2 px-3 py-1 rounded-full bg-primary/10 border border-primary/20">
                <div className="w-2 h-2 rounded-full bg-primary animate-pulse" />
                <span className="text-xs font-medium text-primary">Online</span>
              </div>

              {/* Notifications */}
              <Button variant="ghost" size="icon" className="relative">
                <Bell className="h-5 w-5" />
                <span className="absolute top-1 right-1 w-2 h-2 bg-destructive rounded-full" />
              </Button>

              {/* Messages */}
              <Button variant="ghost" size="icon">
                <MessageSquare className="h-5 w-5" />
              </Button>

              {/* User Menu */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button variant="ghost" className="flex items-center gap-2 px-2">
                    <Avatar className="h-8 w-8">
                      <AvatarImage src={profile?.avatar_url || ''} />
                      <AvatarFallback className="bg-primary text-primary-foreground text-sm">
                        {profile?.full_name ? getInitials(profile.full_name) : 'A'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="hidden md:block text-left">
                      <p className="text-sm font-medium text-foreground">
                        {profile?.full_name || 'Agent'}
                      </p>
                      <p className="text-xs text-muted-foreground">Agent Support</p>
                    </div>
                    <ChevronDown className="h-4 w-4 text-muted-foreground hidden md:block" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel>My Account</DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={() => navigate(`${langPrefix}/agent/settings`)}>
                    <User className="mr-2 h-4 w-4" />
                    My Profile
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`${langPrefix}/agent/settings`)}>
                    <Settings className="mr-2 h-4 w-4" />
                    Settings
                  </DropdownMenuItem>
                  <DropdownMenuItem onClick={() => navigate(`${langPrefix}/agent/qa`)}>
                    <Activity className="mr-2 h-4 w-4" />
                    My Performance
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem onClick={handleSignOut} className="text-destructive">
                    <LogOut className="mr-2 h-4 w-4" />
                    Sign Out
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          </div>
        </header>

        {/* Main Layout */}
        <div className="flex flex-1 overflow-hidden">
          {/* Main Content */}
          <main className="flex-1 overflow-y-auto">
            <div className="p-4 lg:p-6">
              {children}
            </div>
          </main>
        </div>
      </div>
    </div>
  );
}
